import type { Dispatch } from "react"
import { useEffect, useRef, useState } from "react"

import type { SegmentAnalysisAction, SegmentAnalysisState } from "../hooks/use-segment-analysis"
import type {
  AddressableLine,
  Segment,
  SegmentAnalysisDataSource,
  SegmentAnalysisRole,
  SegmentAnalysisRunMode,
  SegmentClaimRegistry,
  SegmentDetailTab,
  SegmentPendingChangesPayload,
  SegmentReport,
} from "../types"
import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search"
import { AlertTriangle, Check, PenLine, RefreshCw, X, Zap } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { ClaimsEvidenceView } from "./claims-evidence-view"
import { CountUp } from "./count-up"
import { SegmentDetailTabs } from "./segment-detail-tabs"
import { SegmentPendingChanges } from "./segment-pending-changes"
import { marketColors } from "../data/mock-segment-analysis-data"
import { DEFAULT_ADDRESSABLE_LINES } from "../types"
import { COMPANY_NAME } from "../utils/branding"
import { segmentAddressableTotal } from "../utils/segment-analysis-utils"

type SegmentDetailProps = {
  /** User-hidden detail tabs (#2069 D2). */
  hiddenDetailTabs?: SegmentDetailTab[]
  addressableLines?: AddressableLine[]
  canFavouriteCompany: boolean
  canRun: boolean
  claimRegistry?: SegmentClaimRegistry | null
  /** Segment owner only: may edit sales / addressable HeroStat pens. */
  canEditMetrics?: boolean
  /** Admin-only: may transfer this segment's ownership (§5.7). */
  canReassignOwner?: boolean
  /** Current user's AAD oid — used to flag self-review on their own proposals. */
  currentUserOid?: string | null
  /** Whether the portfolio is backed by live data (drives CCP summary blurb fetch). */
  dataSource?: SegmentAnalysisDataSource
  dispatch: Dispatch<SegmentAnalysisAction>
  favouriteCompanies: Set<string>
  onDecide: (decision: "approve" | "reject", changeId?: string) => void
  onReassignOwner?: (segmentId: string, ownerName: string, ownerOid?: string | null) => void
  onReclassifyCompany?: (
    segmentId: string,
    companyName: string,
    next: "customer" | "prospect"
  ) => void
  /** Persist manual sales / addressable estimates (segment owner only). */
  onUpdateManualMetrics?: (
    segmentId: string,
    input: { sales?: number; addressable?: Record<string, number> }
  ) => Promise<void>
  onStartRun: (mode: SegmentAnalysisRunMode) => void
  onToggleCompanyFavourite: (companyName: string) => void
  pendingChanges: SegmentPendingChangesPayload | null
  report: SegmentReport | null
  role: SegmentAnalysisRole
  segment: Segment
  state: SegmentAnalysisState
  onAnalyzeCompany?: (companyName: string) => void
  /** Opens a customer's Customer Centric Plan (Customers tab CTA). */
  onOpenCcp?: (companyName: string, options?: { autoDraft?: boolean }) => void
  /** Opens a competitor's Competitor Plan (Competitors tab CTA). */
  onOpenCompetitorPlan?: (companyName: string) => void
}

export function SegmentDetail({
  hiddenDetailTabs,
  addressableLines = DEFAULT_ADDRESSABLE_LINES,
  canFavouriteCompany,
  canRun,
  canEditMetrics: canEditMetricsProp = false,
  canReassignOwner = false,
  claimRegistry,
  currentUserOid,
  dataSource,
  dispatch,
  favouriteCompanies,
  onDecide,
  onReassignOwner,
  onReclassifyCompany,
  onUpdateManualMetrics,
  onStartRun,
  onToggleCompanyFavourite,
  pendingChanges,
  report,
  role,
  segment,
  state,
  onAnalyzeCompany,
  onOpenCcp,
  onOpenCompetitorPlan,
}: SegmentDetailProps) {
  const color = marketColors[segment.marketName] ?? "#94A3B8"
  const amTotal = segmentAddressableTotal(segment)
  const shareVsAm = amTotal > 0 ? Math.round((segment.sales / amTotal) * 1000) / 10 : segment.share
  const [focusedClaimId, setFocusedClaimId] = useState<string | null>(null)
  const [editingMetric, setEditingMetric] = useState<"sales" | "addressable" | null>(null)
  const [metricDraft, setMetricDraft] = useState("")
  const [savingMetric, setSavingMetric] = useState(false)
  const canEditMetrics = Boolean(onUpdateManualMetrics) && canEditMetricsProp
  const primaryAmKey = addressableLines[0]?.key ?? "total"

  const beginEditMetric = (metric: "sales" | "addressable") => {
    setMetricDraft(metric === "sales" ? String(segment.sales) : String(amTotal || ""))
    setEditingMetric(metric)
  }

  const cancelEditMetric = () => {
    setEditingMetric(null)
    setSavingMetric(false)
  }

  const saveEditMetric = async () => {
    if (!editingMetric || savingMetric) return
    const value = Number(metricDraft)
    if (!Number.isFinite(value) || value < 0) return
    setSavingMetric(true)
    try {
      if (editingMetric === "sales") {
        await onUpdateManualMetrics?.(segment.id, { sales: value })
      } else {
        // Hygiene UI edits the rolled-up total, but persistence must keep every
        // existing addressable line key (e.g. plastics + boxBuild). Scale the
        // current breakdown to the new total; only seed primaryAmKey when empty.
        const existing = segment.addressable ?? {}
        const keys = Object.keys(existing)
        let addressable: Record<string, number>
        if (keys.length === 0) {
          addressable = { [primaryAmKey]: value }
        } else if (amTotal > 0) {
          const scale = value / amTotal
          addressable = Object.fromEntries(keys.map((key) => [key, (existing[key] || 0) * scale]))
        } else {
          addressable = Object.fromEntries(keys.map((key, index) => [key, index === 0 ? value : 0]))
        }
        await onUpdateManualMetrics?.(segment.id, { addressable })
      }
      setEditingMetric(null)
    } catch {
      // Toast is shown by the mutation helper; keep the inline editor open.
    } finally {
      setSavingMetric(false)
    }
  }

  return (
    <section
      className={cn(
        "bg-card grid gap-5 rounded-xl border p-4 shadow-sm transition-[border-color,box-shadow] sm:p-5",
        state.flashChanges ? "border-primary/40 ring-primary/15 ring-2" : "border-border"
      )}
    >
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-md px-2.5 py-1 text-xs font-semibold"
              style={{ background: `${color}1f`, color }}
            >
              {segment.marketName}
            </span>
          </div>
          <h2 className="text-foreground mt-3 text-2xl font-semibold tracking-normal">
            {segment.name}
          </h2>
          <p className="text-muted-foreground mt-2 max-w-4xl text-sm leading-6">
            {report?.summary.executiveSummary[0] ?? segment.description}
          </p>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <HeroStat
              label="Addressable market"
              subLabel="Manual estimate"
              value={`${amTotal} MEUR`}
              editing={
                editingMetric === "addressable"
                  ? {
                      draft: metricDraft,
                      unit: "MEUR",
                      saving: savingMetric,
                      onDraftChange: setMetricDraft,
                      onSave: () => void saveEditMetric(),
                      onCancel: cancelEditMetric,
                    }
                  : undefined
              }
              onEdit={canEditMetrics ? () => beginEditMetric("addressable") : undefined}
            />
            <HeroStat
              label="Growth CAGR 5y"
              subLabel={segment.cagr > 5 ? "Above portfolio avg" : "In line with portfolio"}
              value={`${segment.cagr}%`}
            />
            <HeroStat
              label={`${COMPANY_NAME} sales`}
              subLabel="Estimate · updated manually"
              value={`${segment.sales} MEUR`}
              editing={
                editingMetric === "sales"
                  ? {
                      draft: metricDraft,
                      unit: "MEUR",
                      saving: savingMetric,
                      onDraftChange: setMetricDraft,
                      onSave: () => void saveEditMetric(),
                      onCancel: cancelEditMetric,
                    }
                  : undefined
              }
              onEdit={canEditMetrics ? () => beginEditMetric("sales") : undefined}
            />
            <HeroStat
              label="Market share"
              subLabel="Sales / addressable market"
              value={`${shareVsAm}%`}
            />
          </dl>
        </div>
        <aside className="bg-muted/40 border-border w-full rounded-lg border p-4 xl:w-80">
          <OwnerRow
            canReassign={canReassignOwner && Boolean(onReassignOwner)}
            owner={segment.owner}
            ownerOid={segment.ownerOid}
            onReassign={(name, oid) => onReassignOwner?.(segment.id, name, oid)}
          />
          <InfoRow label="Last review" value={segment.lastRunDate} />
          <InfoRow label="Status" value={segment.runStatus === "ok" ? "OK" : "Needs review"} />
          {canRun ? (
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button className="font-semibold" onClick={() => onStartRun("full")}>
                <Zap className="size-4" />
                Full
              </Button>
              <Button variant="outline" onClick={() => onStartRun("partial")}>
                <RefreshCw className="size-4" />
                Partial
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground border-border mt-4 rounded-md border border-dashed px-3 py-2 text-xs">
              {role === "viewer"
                ? "Read-only access — agent runs are disabled for the Sales viewer role."
                : "You can only run agents on segments you own."}
            </p>
          )}
        </aside>
      </div>
      <LowTrustWarning verification={report?.grounded?.verification} />
      <SegmentPendingChanges
        canDecide={canRun}
        pending={pendingChanges}
        runByYou={Boolean(
          currentUserOid && pendingChanges?.runBy && pendingChanges.runBy === currentUserOid
        )}
        onDecide={onDecide}
      />
      <ClaimsEvidenceView
        registry={claimRegistry}
        selectedClaimId={focusedClaimId}
        onSelectedClaimIdChange={setFocusedClaimId}
      />
      <SegmentDetailTabs
        canFavourite={canFavouriteCompany}
        claimRegistry={claimRegistry}
        companyClassifications={segment.companyClassifications}
        customerFilter={state.customerFilter}
        dataSource={dataSource}
        detailTab={state.detailTab}
        dispatch={dispatch}
        entitySearch={state.entitySearch}
        favouriteCompanies={favouriteCompanies}
        hiddenTabs={hiddenDetailTabs}
        pendingChanges={pendingChanges}
        report={report}
        role={role}
        segment={segment}
        onAnalyzeCompany={onAnalyzeCompany}
        onOpenCcp={onOpenCcp}
        onOpenClaim={setFocusedClaimId}
        onOpenCompetitorPlan={onOpenCompetitorPlan}
        onToggleFavourite={onToggleCompanyFavourite}
        onReclassify={
          canRun && onReclassifyCompany
            ? (companyName, next) => onReclassifyCompany(segment.id, companyName, next)
            : undefined
        }
      />
    </section>
  )
}

/**
 * Advisory banner shown above the approval panel when the latest run leans on
 * low-trust sources (10x M3.3). Non-blocking — it never gates approval, only
 * prompts a review. `lowTrust >= 3` uses a heavier (destructive) treatment.
 */
function LowTrustWarning({ verification }: { verification?: { lowTrust: number; total: number } }) {
  const lowTrust = verification?.lowTrust ?? 0
  if (lowTrust <= 0) return null
  const total = verification?.total ?? 0
  const strong = lowTrust >= 3
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm",
        strong
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : "border-warning/30 bg-warning/10 text-warning-foreground"
      )}
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>
        {lowTrust} of {total} cited sources are low-trust — review before approving.
      </span>
    </div>
  )
}

function HeroStat({
  label,
  subLabel,
  value,
  onEdit,
  editing,
}: {
  label: string
  subLabel: string
  value: string
  /** Icon-only edit affordance (same pattern as owner reassign). */
  onEdit?: () => void
  /** When set, the displayed value is replaced by an inline numeric editor. */
  editing?: {
    draft: string
    unit: string
    saving: boolean
    onDraftChange: (next: string) => void
    onSave: () => void
    onCancel: () => void
  }
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const isEditing = Boolean(editing)

  useEffect(() => {
    if (!isEditing) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isEditing])

  return (
    <div className="bg-muted/50 relative rounded-lg p-3">
      <div className="flex items-start justify-between gap-2">
        <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
        {onEdit && !editing ? (
          <Button
            aria-label={`Edit ${label}`}
            className="text-muted-foreground hover:text-primary -mt-1 -mr-1 size-8 shrink-0"
            size="icon"
            title={`Edit ${label}`}
            variant="ghost"
            onClick={onEdit}
          >
            <PenLine className="size-3.5" />
          </Button>
        ) : null}
      </div>
      <dd className="text-foreground mt-1 text-lg font-semibold">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              aria-label={label}
              className="border-input bg-background focus-visible:border-primary focus-visible:ring-primary/30 h-8 w-full max-w-[7.5rem] min-w-0 rounded-md border px-2 text-lg font-semibold tabular-nums outline-none focus-visible:ring-2"
              disabled={editing.saving}
              inputMode="decimal"
              min={0}
              step="any"
              type="number"
              value={editing.draft}
              onChange={(event) => editing.onDraftChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  editing.onSave()
                } else if (event.key === "Escape") {
                  event.preventDefault()
                  editing.onCancel()
                }
              }}
            />
            <span className="text-muted-foreground shrink-0 text-sm font-medium">
              {editing.unit}
            </span>
            <Button
              aria-label={`Save ${label}`}
              className="text-primary size-8 shrink-0"
              disabled={editing.saving}
              size="icon"
              title="Save"
              variant="ghost"
              onClick={editing.onSave}
              onMouseDown={(event) => event.preventDefault()}
            >
              <Check className="size-3.5" />
            </Button>
            <Button
              aria-label={`Cancel editing ${label}`}
              className="text-muted-foreground size-8 shrink-0"
              disabled={editing.saving}
              size="icon"
              title="Cancel"
              variant="ghost"
              onClick={editing.onCancel}
              onMouseDown={(event) => event.preventDefault()}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        ) : (
          <CountUp value={value} />
        )}
      </dd>
      <dd className="text-muted-foreground text-xs">{subLabel}</dd>
    </div>
  )
}

// DESIGN.md wow #2: inputs focus to the orange primary glow, not the default ring.
// Mirrors the wizard's shared input style so the module's fields are consistent.
function OwnerRow({
  canReassign,
  owner,
  ownerOid,
  onReassign,
}: {
  canReassign: boolean
  owner: string
  ownerOid?: string | null
  onReassign: (ownerName: string, ownerOid?: string | null) => void
}) {
  const [editing, setEditing] = useState(false)

  if (editing) {
    // Pick the new owner from the M365 directory (same DirectorySearch the share
    // dialogs use) so we capture a real AAD oid + display name — no hand-typed
    // names or object ids. Selecting a person reassigns immediately.
    return (
      <div className="border-border grid gap-2 border-b py-2 last:border-b-0">
        <span className="text-muted-foreground text-sm">Reassign owner</span>
        <DirectorySearch
          placeholder="Search people…"
          selectedOids={ownerOid ? [ownerOid] : []}
          types={["users"]}
          onSelect={(result: DirectorySearchResult) => {
            onReassign(result.displayName, result.oid)
            setEditing(false)
          }}
        />
        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <div className="border-border flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground">Segment owner</span>
      <span className="flex items-center gap-2">
        <strong className="text-foreground text-right font-semibold">{owner}</strong>
        {canReassign ? (
          <Button
            aria-label="Reassign segment owner"
            className="text-muted-foreground hover:text-primary -my-2 size-8"
            size="icon"
            title="Reassign owner (admin)"
            variant="ghost"
            onClick={() => setEditing(true)}
          >
            <PenLine className="size-3.5" />
          </Button>
        ) : null}
      </span>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <strong className="text-foreground text-right font-semibold">{value}</strong>
    </div>
  )
}
